import isNumber from 'lodash/isNumber';
import throttle from 'lodash/throttle';
import { useEffect, useMemo, useState } from 'react';

import { SizeType } from 'craft/components/Size';
import { BuiltInComponents, CRAFT_ROOT_ID, isTooltipDocument } from 'craft/constants';
import { useGetVariantRules, useResolver, useResponsiveCss, useResponsiveProps } from 'craft/hooks';
import { useViewportDom } from 'craft/state/viewport/selectors';
import { getDomNodeFromDocument } from 'craft/utils/viewport';

interface useViewportDimensionsProps {
  rootNodeName: string;
}

export const useViewportDimensions = ({ rootNodeName }: useViewportDimensionsProps) => {
  const { viewportWrapperEl } = useViewportDom();
  const isModal = rootNodeName === BuiltInComponents.Modal || isTooltipDocument(rootNodeName);

  const resolver = useResolver(rootNodeName);
  const { variantKey } = resolver?.toolbox || {};

  const variantRules = useGetVariantRules(['ROOT']);
  const { '--width': fallbackModalWidth = 'initial' } = isModal
    ? variantRules?.selectors?.[variantKey ?? '']?.rules || {}
    : {};

  const { responsiveProps } = useResponsiveProps([CRAFT_ROOT_ID]);
  const { responsiveCss } = useResponsiveCss([CRAFT_ROOT_ID]);

  const { marginLeft = '0px', marginTop = '0px', marginRight = '0px', marginBottom = '0px' } =
    responsiveCss ?? {};

  const { width, height } = responsiveProps || {};

  const isRelativeWidth = useMemo(() => (width as string)?.includes('%'), [width]);
  const isRelativeHeight = useMemo(() => (height as string)?.includes('%'), [height]);

  // TODO: this used to be in a mixed up useEffect, can we improve?
  const rootNodeDomRef = getDomNodeFromDocument(CRAFT_ROOT_ID);

  const [widthOverride, setWidthOverride] = useState<string | undefined>(undefined);
  const [heightOverride, setHeightOverride] = useState<string | undefined>(undefined);

  const getWidthOverride = () => {
    const getCalcString = (widthValue: string) =>
      `calc(${widthValue} + ${isNumber(marginLeft) ? `${marginLeft}px` : marginLeft || '0px'} + ${
        isNumber(marginRight) ? `${marginRight}px` : marginRight || '0px'
      })`;

    if (!isModal || isRelativeWidth) {
      return undefined;
    }

    if (typeof width !== 'undefined' && width !== SizeType.Auto) {
      return getCalcString(width || fallbackModalWidth);
    }

    if (!rootNodeDomRef) {
      return undefined;
    }

    const { offsetWidth } = rootNodeDomRef;

    return getCalcString(`${offsetWidth}px`);
  };

  const getHeightOverride = () => {
    const getCalcString = (heightValue: string) =>
      `calc(${heightValue} + ${isNumber(marginTop) ? `${marginTop}px` : marginTop || '0px'} + ${
        isNumber(marginBottom) ? `${marginBottom}px` : marginBottom || '0px'
      })`;

    if (isModal && isRelativeHeight) {
      return undefined;
    }

    if (typeof height !== 'undefined' && height !== SizeType.Auto) {
      return getCalcString(height);
    }

    if (!rootNodeDomRef) {
      return undefined;
    }

    const { offsetHeight } = rootNodeDomRef;

    return getCalcString(`${offsetHeight}px`);
  };

  useEffect(() => {
    setWidthOverride(getWidthOverride());
    setHeightOverride(getHeightOverride());
  }, []);

  useEffect(() => {
    if (!viewportWrapperEl) {
      return () => null;
    }

    // make sure to recalculate overrides when the viewportContainer is resized
    const callback = throttle(() => {
      setWidthOverride(getWidthOverride());
      setHeightOverride(getHeightOverride());
    }, 50);
    const observer = new ResizeObserver(callback);
    observer.observe(viewportWrapperEl);

    return () => observer.disconnect();
  }, [viewportWrapperEl]);

  return {
    widthOverride,
    heightOverride,
  };
};
